/**
 * Inicio: el panel.
 *
 * Responde de un vistazo a las cuatro preguntas que uno se hace al abrir una
 * aplicación de finanzas:
 *
 *  1. ¿Cuánto tengo? → disponible real (descontando lo apartado para metas).
 *  2. ¿Cómo voy este mes? → gasto, ingreso y comparación con el mes anterior.
 *  3. ¿En qué se me va? → desglose por categoría.
 *  4. ¿Qué viene? → próximos cobros fijos y movimientos recientes.
 *
 * Todo con cifras reales del servidor. Nada de datos de ejemplo ni de promesas.
 */

import { useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, CalendarClock, HandCoins, Landmark, Plus, TrendingUp, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  currentMonthKey,
  formatDateLabel,
  formatMonthLabel,
  kindLabel,
  relativeDayLabel,
  signedAmountForKind,
  todayISO,
} from '@eldescuadre/core';

import {
  getAccounts,
  getCategoryBreakdown,
  getDebtsTotals,
  getFiadoSummary,
  getPendingRecurring,
  getSeries,
  getSummary,
  getTransactions,
  getUpcomingRecurring,
} from '../lib/endpoints';
import { useSession } from '../lib/session';
import { Card, EmptyState, ErrorNote, Loading, Money, Progress, Stat } from '../components/ui';
import { AssetDistribution } from '../components/BalanceCharts';

export function DashboardPage(): React.JSX.Element {
  const user = useSession((s) => s.user);
  const monthKey = currentMonthKey();

  const summary = useQuery({
    queryKey: ['summary', monthKey],
    queryFn: () => getSummary(monthKey),
  });
  const categories = useQuery({
    queryKey: ['reports', 'categories', monthKey],
    queryFn: () => getCategoryBreakdown(monthKey),
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });
  const recent = useQuery({
    queryKey: ['transactions', 'recent'],
    queryFn: () => getTransactions({ pageSize: 5 }),
  });
  const fiado = useQuery({ queryKey: ['fiado', 'summary'], queryFn: getFiadoSummary });
  const debtsTotals = useQuery({ queryKey: ['debts', 'totals'], queryFn: getDebtsTotals });
  const upcoming = useQuery({
    queryKey: ['recurring', 'upcoming'],
    queryFn: () => getUpcomingRecurring(15),
  });
  const pending = useQuery({ queryKey: ['recurring', 'pending'], queryFn: getPendingRecurring });
  const series = useQuery({
    queryKey: ['reports', 'series', 6],
    queryFn: () => getSeries(6, monthKey),
  });

  const data = summary.data;

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm capitalize text-ed-text-muted">{formatDateLabel(todayISO())}</p>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">
            Hola, {user?.displayName?.split(' ')[0] ?? 'bienvenido'}
          </h1>
          <p className="mt-0.5 text-sm text-ed-text-silver capitalize">
            {formatMonthLabel(monthKey)}
          </p>
        </div>

        <Link
          to="/nuevo"
          className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4"
          aria-label="Registrar un movimiento"
        >
          <Plus size={18} aria-hidden="true" />
          Registrar
        </Link>
      </header>

      {summary.isLoading && <Loading label="Calculando tus cifras…" />}

      {summary.isError && (
        <ErrorNote
          message="No se pudieron cargar tus cifras."
          hint="Comprueba tu conexión. Si el problema sigue, vuelve a entrar."
        />
      )}

      {data && (
        <>
          {/* Los tres números que importan. */}
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat
              label="Disponible para gastar"
              value={<Money amount={data.spendable} />}
              hint={
                data.reservedForGoals > 0 ? (
                  <>
                    Apartados <Money amount={data.reservedForGoals} /> para tus metas
                  </>
                ) : (
                  'En tus cuentas de activo'
                )
              }
              tone="gold"
            />
            <Stat
              label="Gastado este mes"
              value={<Money amount={-data.monthExpense} signed />}
              hint={
                data.expenseChangePercent !== null ? (
                  <span
                    className={
                      data.expenseChangePercent > 0 ? 'text-ed-coral-light' : 'text-ed-success'
                    }
                  >
                    {data.expenseChangePercent > 0 ? '▲' : '▼'}{' '}
                    {Math.abs(Math.round(data.expenseChangePercent))} % frente al mes pasado
                  </span>
                ) : (
                  'Sin mes anterior con el que comparar'
                )
              }
            />
            <Stat
              label="Patrimonio neto"
              value={<Money amount={data.netWorth} />}
              hint={
                data.liabilities > 0 ? (
                  <>
                    Debes <Money amount={data.liabilities} /> en total
                  </>
                ) : (
                  'No debes nada. Bien ahí.'
                )
              }
              tone={data.netWorth >= 0 ? 'positive' : 'negative'}
            />
          </div>

          {/* Aviso cuando hay cobros fijos esperando. */}
          {(pending.data?.pending ?? 0) > 0 && (
            <Link to="/recurrentes" className="block">
              <div className="flex items-center gap-3 rounded-ed border border-ed-cyan/30 bg-ed-cyan/10 px-3 py-2.5 text-sm text-ed-cyan-light">
                <CalendarClock size={18} className="shrink-0" aria-hidden="true" />
                <span>
                  Tienes <strong>{pending.data?.pending}</strong>{' '}
                  {pending.data?.pending === 1 ? 'cobro fijo pendiente' : 'cobros fijos pendientes'} de
                  registrar. Toca para verlos.
                </span>
              </div>
            </Link>
          )}

          {/* Cuentas */}
          {accounts.data && accounts.data.accounts.length > 0 && (
            <Card
              title="Tus cuentas"
              action={
                <Link to="/cuentas" className="text-xs text-ed-cyan-light hover:underline">
                  Ver todas
                </Link>
              }
            >
              <ul className="divide-y divide-ed-bg-border/25">
                {accounts.data.accounts.slice(0, 4).map((account) => (
                  <li key={account.id} className="flex items-center justify-between gap-3 py-2">
                    <span className="flex min-w-0 items-center gap-2">
                      <Wallet size={15} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
                      <span className="truncate text-sm">{account.name}</span>
                    </span>
                    <Money
                      amount={account.balance}
                      className={`text-sm font-semibold ${
                        account.balance < 0 ? 'text-ed-coral-light' : ''
                      }`}
                    />
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* En qué se va el dinero */}
          <Card
            title="En qué se te fue"
            action={
              <Link to="/informes" className="text-xs text-ed-cyan-light hover:underline">
                Informes
              </Link>
            }
          >
            {categories.isLoading && <Loading />}

            {categories.data && categories.data.categories.length === 0 && (
              <EmptyState
                title="Todavía no hay gastos este mes"
                message="Cuando registres el primero, aquí verás en qué categorías se va tu dinero."
                action={
                  <Link to="/nuevo" className="ed-btn-primary !w-auto !px-4">
                    Registrar un gasto
                  </Link>
                }
              />
            )}

            {categories.data && categories.data.categories.length > 0 && (
              <ul className="space-y-3">
                {categories.data.categories.slice(0, 6).map((row) => (
                  <li key={row.categoryId ?? row.name}>
                    <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                      <span className="flex items-center gap-2 truncate">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: row.color }}
                          aria-hidden="true"
                        />
                        {row.name}
                      </span>
                      <span className="flex shrink-0 items-baseline gap-2">
                        <Money amount={row.total} className="font-semibold" />
                        <span className="text-xs text-ed-text-muted">
                          {Math.round(row.percentOfTotal)} %
                        </span>
                      </span>
                    </div>
                    <Progress percent={row.percentOfTotal} label={`${row.name}: ${Math.round(row.percentOfTotal)} por ciento del gasto`} />
                    {row.changePercent !== null && Math.abs(row.changePercent) >= 20 && (
                      <p
                        className={`mt-1 text-xs ${
                          row.changePercent > 0 ? 'text-ed-coral-light' : 'text-ed-success'
                        }`}
                      >
                        {row.changePercent > 0 ? 'Subió' : 'Bajó'}{' '}
                        {Math.abs(Math.round(row.changePercent))} % frente al mes pasado
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Últimos movimientos */}
          <Card
            title="Últimos movimientos"
            action={
              <Link to="/movimientos" className="text-xs text-ed-cyan-light hover:underline">
                Ver todos
              </Link>
            }
          >
            {recent.isLoading && <Loading />}

            {recent.data && recent.data.items.length === 0 && (
              <EmptyState
                title="Aún no has registrado nada"
                message="Empieza por el gasto más reciente que recuerdes. Con tres o cuatro ya se ven patrones."
                action={
                  <Link to="/nuevo" className="ed-btn-primary !w-auto !px-4">
                    Registrar el primero
                  </Link>
                }
              />
            )}

            {recent.data && recent.data.items.length > 0 && (
              <ul className="divide-y divide-ed-bg-border/25">
                {recent.data.items.map((transaction) => (
                  <li key={transaction.id} className="flex items-center gap-3 py-2.5">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                        transaction.kind === 'INCOME' || transaction.kind === 'SPLIT_SETTLEMENT'
                          ? 'bg-ed-success/15 text-ed-success'
                          : 'bg-ed-bg-elevated text-ed-text-muted'
                      }`}
                      aria-hidden="true"
                    >
                      {transaction.kind === 'INCOME' ? (
                        <ArrowUpRight size={15} />
                      ) : (
                        <ArrowDownRight size={15} />
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">
                        {transaction.merchant || kindLabel(transaction.kind)}
                      </span>
                      <span className="block text-xs text-ed-text-muted">
                        {relativeDayLabel(transaction.date) ?? formatDateLabel(transaction.date)}
                        {' · '}
                        {kindLabel(transaction.kind)}
                      </span>
                    </span>

                    <Money
                      amount={signedAmountForKind(transaction.kind, transaction.amount)}
                      signed
                      className="shrink-0 text-sm font-semibold"
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Fiado y deudas, resumidos */}
          <div className="grid gap-3 sm:grid-cols-2">
            <Link to="/fiado" className="block">
              <Card className="h-full">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ed-coral/15 text-ed-coral-light">
                    <HandCoins size={20} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-wide text-ed-text-muted">
                      Debes en el fiado
                    </p>
                    <Money
                      amount={fiado.data?.totalDebt ?? 0}
                      className="font-display text-lg font-bold"
                    />
                    {(fiado.data?.overLimit ?? 0) > 0 && (
                      <p className="text-xs text-ed-coral-light">
                        Pasaste el cupo en <Money amount={fiado.data?.overLimit ?? 0} />
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </Link>

            <Link to="/deudas" className="block">
              <Card className="h-full">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ed-cyan/15 text-ed-cyan-light">
                    <Landmark size={20} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-wide text-ed-text-muted">
                      Deudas formales
                    </p>
                    <Money
                      amount={data.debtsTotal}
                      className="font-display text-lg font-bold"
                    />
                    <p className="text-xs text-ed-text-muted">
                      {(debtsTotals.data?.activeCount ?? 0) > 0 ? (
                        <>
                          Cuotas del mes:{' '}
                          <Money amount={debtsTotals.data?.monthlyInstallments ?? 0} />
                        </>
                      ) : (
                        'Sin deudas activas'
                      )}
                    </p>
                  </div>
                </div>
              </Card>
            </Link>
          </div>

          {/* Dónde está el dinero, repartido por cuenta. Es la respuesta a la pregunta
              que se hace al abrir la aplicación. Lo tenía Cuentify y faltaba. */}
          <AssetDistribution monthKey={monthKey} />

          {/* Evolución de los últimos meses */}
          {series.data && series.data.series.some((p) => p.expense > 0) && (
            <Card title="Últimos meses">
              <ul className="space-y-2">
                {series.data.series.map((point) => {
                  const max = Math.max(
                    ...series.data!.series.map((p) => Math.max(p.expense, p.income)),
                    1,
                  );
                  return (
                    <li key={point.monthKey} className="flex items-center gap-3 text-xs">
                      <span className="w-16 shrink-0 capitalize text-ed-text-muted">
                        {formatMonthLabel(point.monthKey).split(' ')[0]}
                      </span>
                      <span className="flex-1 space-y-1">
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2 rounded-full bg-ed-coral/70"
                            style={{ width: `${(point.expense / max) * 100}%` }}
                          />
                          <Money amount={point.expense} className="text-ed-text-silver" />
                        </span>
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2 rounded-full bg-ed-success/70"
                            style={{ width: `${(point.income / max) * 100}%` }}
                          />
                          <Money amount={point.income} className="text-ed-text-muted" />
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-xs text-ed-text-muted">
                <TrendingUp size={12} className="mr-1 inline" aria-hidden="true" />
                Rojo es lo que gastaste; verde, lo que entró. La diferencia es tu flujo.
              </p>
            </Card>
          )}

          {/* Próximos cobros fijos */}
          {upcoming.data && upcoming.data.rules.length > 0 && (
            <Card title="Lo que viene">
              <ul className="divide-y divide-ed-bg-border/25">
                {upcoming.data.rules.map((rule) => (
                  <li key={rule.id} className="flex items-center justify-between gap-3 py-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{rule.description}</span>
                      <span className="block text-xs text-ed-text-muted">
                        {rule.nextRunDate
                          ? (relativeDayLabel(rule.nextRunDate) ?? formatDateLabel(rule.nextRunDate))
                          : 'Sin próximo cobro'}
                      </span>
                    </span>
                    <Money amount={rule.amount} className="shrink-0 text-sm font-semibold" />
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
